import { Input, Table, Tag, Tooltip } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeadingBack from "../../component/HeadingBack";
import SvgIcon from "../../const/SvgIcon";
import { DateFormat, GetLoggedInOrganization } from "../../utils";
import { useRights } from "../../hooks/usePermission";
import {
  fetchOrganizations,
  updateOrganizationStatus,
} from "../../redux/Services/OrganizationServices";
import { useAsyncAction } from "../../hooks/useAsyncAction";

const Organizations = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const ownOrganizationId = GetLoggedInOrganization().OrganizationId;
  // Add / edit need "Users Management" rights (super admin has all)
  const rights = useRights("Users Management");

  const loadList = async () => {
    setLoading(true);
    setList(await fetchOrganizations());
    setLoading(false);
  };

  useEffect(() => {
    loadList();
  }, []);

  // Organization whose status is being changed (shows a spinner on that row)
  const [togglingId, setTogglingId] = useState(null);
  // Locked while a change is running, so a double click doesn't flip it back
  const [handleStatus] = useAsyncAction(async (item) => {
    if (item.OrganizationId === ownOrganizationId || !rights.Edit) return;
    setTogglingId(item.Id);
    try {
      const res = await updateOrganizationStatus(item.Id, item.Status ? 0 : 1);
      if (res) await loadList();
    } finally {
      setTogglingId(null);
    }
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (o) =>
        o.Name?.toLowerCase().includes(q) ||
        o.OrganizationId?.toLowerCase().includes(q)
    );
  }, [list, search]);

  const columns = [
    {
      title: "S.No.",
      key: "sno",
      className: "table_first_col",
      render: (value, item, index) => (page - 1) * 10 + index + 1,
    },
    { title: "Organization ID", dataIndex: "OrganizationId", key: "OrganizationId" },
    {
      title: "Name",
      dataIndex: "Name",
      key: "Name",
      render: (value, item) => (
        <>
          {value} {item.OrganizationId === ownOrganizationId && <Tag color="blue">Yours</Tag>}
        </>
      ),
    },
    { title: "Email", dataIndex: "Email", key: "Email" },
    { title: "Mobile", dataIndex: "Mobile", key: "Mobile" },
    {
      title: "Created",
      dataIndex: "CreatedAt",
      key: "CreatedAt",
      render: (value) => DateFormat(value),
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      className: "table_status_col",
      render: (value, item) => {
        const isOwn = item.OrganizationId === ownOrganizationId;
        const locked = isOwn || !rights.Edit;
        return (
          <Tooltip title={isOwn ? "Your own organization cannot be deactivated" : locked ? "" : "Click to change"}>
            <span
              className={item?.Status ? "status_active" : "status_inactive"}
              style={{ cursor: locked ? "not-allowed" : togglingId ? "wait" : "pointer" }}
              onClick={() => handleStatus(item)}
            >
              {togglingId === item.Id && <span className="btn-spinner mr-5" />}
              {item?.Status ? "Active" : "Inactive"}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      className: "table_action_col",
      render: (value, item) => (
        <>
          <Tooltip title="View details & facilities">
            <span
              className="cursor-pointer"
              onClick={() => navigate(`/organization/view/${item?.Id}`)}
            >
              <EyeOutlined style={{ fontSize: 18 }} />
            </span>
          </Tooltip>
          {rights.Edit && (
            <Tooltip title="Edit">
              <span
                className="cursor-pointer ml-10"
                onClick={() => navigate(`/organization/edit/${item?.Id}`)}
              >
                <SvgIcon.Edit className="scale-icon edit-icon" />
              </span>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  return (
    <div>
      <div className="flex">
        <div>
          <HeadingBack title={"Organizations"} subHeading={`Total ${list.length || 0}`} />
        </div>
        <div className="flex1 flex justify-content-end align-items-center gap10">
          <Input
            allowClear
            placeholder="Search name or ID"
            style={{ maxWidth: 240 }}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          {rights.Add && (
            <Tooltip title="Add">
              <span className="jop_btn" onClick={() => navigate("/organization/add")}>
                Add
                <SvgIcon.Add width={40} height={40} className="cursor-pointer" />
              </span>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="tableResponsive mt-10">
        <Table
          rowKey={(row) => row?.Id ?? row?.id}
          loading={loading}
          className="table antd_table"
          bordered
          columns={columns}
          dataSource={filtered}
          pagination={{
            current: page,
            pageSize: 10,
            onChange: (current) => setPage(current),
            hideOnSinglePage: true,
          }}
        />
      </div>
    </div>
  );
};

export default Organizations;
